import { useStudents } from '@/app/redux/hooks/students';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import { getPhase } from '@/common/utils/scopeAndSequence';
import PlacementTestAttemptTable from '@/dashboard/LearnerView/ActivityFeed/AttemptTables/PlacementTestAttemptTable';
import GenericResultDialogue from '@/dashboard/LearnerView/ActivityFeed/GenericResultDialogue';
import api from '@/db/apiClient';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface PlacementTestDialogueProps {
  result: PlacementTestResult | null;
  onClose: () => void;
}
export default function PlacementTestDialogue({
  result,
  onClose,
}: PlacementTestDialogueProps) {
  const { studentsById } = useStudents();

  const student = result ? studentsById.get(result.studentId) : undefined;

  const [isLoading, setLoading] = useState(false);
  const [placementTest, setPlacementTest] = useState<PlacementTest | null>();

  const description = useMemo(() => {
    if (!placementTest) return '';
    const phase = getPhase(placementTest.phase);
    let description = `Phase ${placementTest.phase}`;
    if (phase) description += ` - ${phase.description}`;
    return description;
  }, [placementTest]);

  useEffect(() => {
    (async () => {
      if (!result?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const placementTest = await api.asEducator.getPlacementTest(
        result.placementTestId,
      );
      if (!placementTest) {
        console.error('placement test not found');
        toast.error('Failed to load placement test result', {
          description: 'Placement test not found',
        });
        onClose();
        return;
      }
      setPlacementTest(placementTest);
      setLoading(false);
    })();
  }, [onClose, result]);

  if (!result || !placementTest || !student) return null;
  return (
    <GenericResultDialogue
      open={!!result && !!placementTest}
      onClose={() => {
        setPlacementTest(null);
        onClose();
      }}
      title={'Placement Test Result'}
      studentName={`${student.studentFirstName} ${student.studentSurname}`}
      description={description}
      isLoading={isLoading}
      startedAt={result.startedAt}
      endedAt={result.endedAt}
      completed={true}
      correctAttempts={
        result.wordResponses.filter((r) =>
          r.analysis.attemptToCorrectDistances.every((d) => !d),
        ).length
      }
      totalAttempts={result.wordResponses.length}
      onExport={undefined}
      onShare={undefined}
      renderAttemptsTable={() => (
        <PlacementTestAttemptTable placementTestResult={result} />
      )}
    />
  );
}
